import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    IconButton,
    Tooltip,
    Stack,
    alpha,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Alert,
    AlertTitle
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import CommonTable from '../UI/Table/CommonTable';
import { addDropDownData, getDropdownData, deleteDropDownData } from '../Queries';

const RatingMetricsDialog = ({ open, onClose, programCode, clientCode }) => {
    const [metrics, setMetrics] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [openMetricForm, setOpenMetricForm] = useState(false);
    const [editingMetric, setEditingMetric] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        metrics: [
            { rating: '5-Excellent', description: '' },
            { rating: '4-Good', description: '' },
            { rating: '3-Average', description: '' },
            { rating: '2-Below Average', description: '' },
            { rating: '1-Poor', description: '' }
        ]
    });
    const [errors, setErrors] = useState({});
    const [alert, setAlert] = useState({ open: false, severity: 'success', message: '' });

    let [userData] = useState(localStorage.getItem("userData"));
    userData = userData ? JSON.parse(userData) : userData;

    useEffect(() => {
        if (open && programCode) {
            loadMetrics();
        }
    }, [open, programCode]);

    const handleCloseAlert = () => {
        setAlert({ open: false, severity: 'success', message: '' });
    };

    const loadMetrics = async () => {
        setIsLoading(true);
        try {
            const allDropdownData = await getDropdownData();
            // Filter for rating metrics that belong to this program
            const ratingMetrics = allDropdownData.filter(
                item => item.type === 'program_rating_metrics' && item.client_code === clientCode
            );

            // Keep the metrics as they are, with full json_data
            const processedMetrics = ratingMetrics.map(metric => ({
                row_id: metric.row_id,
                name: metric.name,
                json_data: metric.json_data,
                metrics: metric.json_data?.metrics || {}
            }));

            setMetrics(processedMetrics);
        } catch (error) {
            console.error('Error loading metrics:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddMetric = () => {
        setEditingMetric(null);
        setFormData({
            name: '',
            metrics: [
                { rating: '5-Excellent', description: '' },
                { rating: '4-Good', description: '' },
                { rating: '3-Average', description: '' },
                { rating: '2-Below Average', description: '' },
                { rating: '1-Poor', description: '' }
            ]
        });
        setErrors({});
        setOpenMetricForm(true);
    };

    const handleEditMetric = (metric) => {
        setEditingMetric(metric);

        // Populate form with existing metric data
        const metricsArray = [
            { rating: '5-Excellent', description: metric.metrics?.['5-Excellent']?.description || '' },
            { rating: '4-Good', description: metric.metrics?.['4-Good']?.description || '' },
            { rating: '3-Average', description: metric.metrics?.['3-Average']?.description || '' },
            { rating: '2-Below Average', description: metric.metrics?.['2-Below Average']?.description || '' },
            { rating: '1-Poor', description: metric.metrics?.['1-Poor']?.description || '' }
        ];

        setFormData({
            name: metric.name,
            metrics: metricsArray
        });

        setErrors({});
        setOpenMetricForm(true);
    };

    const handleDeleteMetric = async (row) => {
        if (window.confirm(`Are you sure you want to delete all ratings for "${row.name}"? This will remove all 5 rating levels.`)) {
            try {
                await deleteDropDownData(row.row_id, 'program_rating_metrics');
                await loadMetrics();
                setAlert({
                    open: true,
                    severity: 'success',
                    message: 'Rating metrics deleted successfully!'
                });
            } catch (error) {
                console.error('Error deleting metrics:', error);
                setAlert({
                    open: true,
                    severity: 'error',
                    message: 'Failed to delete rating metrics. Please try again.'
                });
            }
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handleDescriptionChange = (index, value) => {
        setFormData(prev => ({
            ...prev,
            metrics: prev.metrics.map((metric, i) =>
                i === index ? { ...metric, description: value } : metric
            )
        }));
        if (errors[`description_${index}`]) {
            setErrors(prev => ({
                ...prev,
                [`description_${index}`]: null
            }));
        }
    };

    const validate = () => {
        const newErrors = {};

        // Validate metric name
        if (!formData.name?.trim()) {
            newErrors.name = 'Metric name is required';
        }

        // Validate all 5 descriptions are filled
        formData.metrics.forEach((metric, index) => {
            if (!metric.description?.trim()) {
                newErrors[`description_${index}`] = 'Description is required';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveMetric = async () => {
        if (!validate()) return;

        try {
            // Create a single object with all metrics in json_data
            const metricsObject = {};
            formData.metrics.forEach(metric => {
                metricsObject[metric.rating] = {
                    description: metric.description,
                    rating: metric.rating
                };
            });

            const metricData = {
                name: formData.name,
                data_type: 'program_rating_metrics',
                client_code: clientCode,
                email: userData.email,
                json_data: {
                    program_code: programCode,
                    metrics: metricsObject
                }
            };

            // If editing, add the id to update
            if (editingMetric) {
                metricData.id = editingMetric.row_id;
            }

            await addDropDownData(metricData, !!editingMetric);
            await loadMetrics();
            setOpenMetricForm(false);
            setAlert({
                open: true,
                severity: 'success',
                message: editingMetric ? 'Rating metrics updated successfully!' : 'Rating metrics added successfully!'
            });
        } catch (error) {
            console.error('Error saving metrics:', error);
            setAlert({
                open: true,
                severity: 'error',
                message: 'Failed to save rating metrics. Please try again.'
            });
        }
    };

    const columns = [
        { key: 'serialNo', label: 'S.No', minWidth: 50, align: 'center' },
        { key: 'name', label: 'Metric Name', minWidth: 120 },
        { key: '5-Excellent', label: '5 - Excellent', minWidth: 140 },
        { key: '4-Good', label: '4 - Good', minWidth: 140 },
        { key: '3-Average', label: '3 - Average', minWidth: 140 },
        { key: '2-Below Average', label: '2 - Below Average', minWidth: 140 },
        { key: '1-Poor', label: '1 - Poor', minWidth: 140 },
        { key: 'actions', label: 'Actions', minWidth: 100, align: 'center' }
    ];

    const renderCell = (columnKey, value, row, index) => {
        switch (columnKey) {
            case 'serialNo':
                return (
                    <Box sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        fontSize: '0.85rem'
                    }}>
                        {index + 1}
                    </Box>
                );

            case 'name':
                return (
                    <Box sx={{
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        color: '#2c3e50'
                    }}>
                        {row.name}
                    </Box>
                );

            case '5-Excellent':
            case '4-Good':
            case '3-Average':
            case '2-Below Average':
            case '1-Poor':
                return (
                    <Box sx={{
                        fontSize: '0.7rem',
                        color: 'text.secondary',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: 1.3
                    }}>
                        {row.metrics?.[columnKey]?.description || '-'}
                    </Box>
                );

            case 'actions':
                return (
                    <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="Edit" arrow placement="top">
                            <IconButton
                                onClick={() => handleEditMetric(row)}
                                size="small"
                                sx={{
                                    padding: '5px',
                                    backgroundColor: alpha('#cc4049', 0.1),
                                    color: '#cc4049',
                                    border: `1px solid ${alpha('#cc4049', 0.3)}`,
                                    borderRadius: '6px',
                                    '&:hover': {
                                        backgroundColor: alpha('#cc4049', 0.2),
                                        transform: 'scale(1.1)',
                                        boxShadow: '0 2px 8px rgba(204, 64, 73, 0.3)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow placement="top">
                            <IconButton
                                onClick={() => handleDeleteMetric(row)}
                                size="small"
                                sx={{
                                    padding: '5px',
                                    backgroundColor: alpha('#f44336', 0.1),
                                    color: '#f44336',
                                    border: `1px solid ${alpha('#f44336', 0.3)}`,
                                    borderRadius: '6px',
                                    '&:hover': {
                                        backgroundColor: alpha('#f44336', 0.2),
                                        transform: 'scale(1.1)',
                                        boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );

            default:
                return value;
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        fontFamily: 'Poppins',
                        maxWidth: '95vw'
                    }
                }}
            >
                <DialogTitle sx={{
                    borderBottom: '1px solid #e0e0e0',
                    pb: 2,
                    fontFamily: 'Poppins',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="h6" sx={{ fontFamily: 'Poppins', fontWeight: 600 }}>
                        Rating Metrics - {programCode}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    {/* Alert Section */}
                    {alert.open && (
                        <Alert
                            severity={alert.severity}
                            onClose={handleCloseAlert}
                            sx={{
                                borderRadius: 2,
                                mb: 2,
                                '& .MuiAlert-message': {
                                    width: '100%'
                                }
                            }}
                        >
                            <AlertTitle sx={{ fontWeight: 600 }}>
                                {alert.severity === 'success' ? 'Success' : 'Error'}
                            </AlertTitle>
                            {alert.message}
                        </Alert>
                    )}

                    <Box sx={{ mb: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddMetric}
                            size="small"
                            sx={{
                                textTransform: "none",
                                fontWeight: 500,
                                borderRadius: 1,
                                px: 2,
                                fontSize: '13px',
                                py: 0.75,
                                background: "linear-gradient(45deg, #0a0068 30%, #cc4049 90%)",
                                "&:hover": {
                                    background: "linear-gradient(45deg, #1565c0 30%, #0a0068 90%)",
                                },
                            }}
                        >
                            Add Rating Metric
                        </Button>
                    </Box>

                    <Box sx={{
                        '& .MuiTableCell-root': {
                            padding: '8px 12px !important'
                        }
                    }}>
                        <CommonTable
                            columns={columns}
                            data={metrics}
                            renderCustomCell={renderCell}
                            isLoading={isLoading}
                            emptyMessage="No rating metrics created yet"
                            searchable={false}
                            pageSize={5}
                        />
                    </Box>
                </DialogContent>

                <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
                    <Button
                        onClick={onClose}
                        sx={{
                            textTransform: 'none',
                            fontFamily: 'Poppins',
                            color: '#666'
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Metric Form Dialog */}
            <Dialog
                open={openMetricForm}
                onClose={() => setOpenMetricForm(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        fontFamily: 'Poppins'
                    }
                }}
            >
                <DialogTitle sx={{
                    borderBottom: '1px solid #e0e0e0',
                    pb: 2,
                    fontFamily: 'Poppins'
                }}>
                    {editingMetric ? 'Edit Rating Metrics (All 5 Levels)' : 'Add Rating Metrics (All 5 Levels)'}
                </DialogTitle>

                <DialogContent sx={{ p: 3, mt: 2 }}>
                    <Stack spacing={2}>
                        {/* Alert Section */}
                        {alert.open && (
                            <Alert
                                severity={alert.severity}
                                onClose={handleCloseAlert}
                                sx={{
                                    borderRadius: 2,
                                    '& .MuiAlert-message': {
                                        width: '100%'
                                    }
                                }}
                            >
                                <AlertTitle sx={{ fontWeight: 600 }}>
                                    {alert.severity === 'success' ? 'Success' : 'Error'}
                                </AlertTitle>
                                {alert.message}
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            label="Metric Name"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                            required
                            placeholder="e.g., Communication Skills, Technical Expertise"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: 'Poppins',
                                }
                            }}
                            size="small"
                        />

                        <Box sx={{ mb: 1, mt: 2 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'Poppins', color: '#666', mb: 2 }}>
                                Please provide descriptions for all 5 rating levels:
                            </Typography>
                        </Box>

                        {formData.metrics.map((metric, index) => (
                            <Stack key={index} direction="row" spacing={2} alignItems="flex-start">
                                <TextField
                                    label="Rating"
                                    value={metric.rating}
                                    disabled
                                    sx={{
                                        width: '200px',
                                        '& .MuiOutlinedInput-root': {
                                            fontFamily: 'Poppins',
                                            backgroundColor: '#f5f5f5'
                                        }
                                    }}
                                    size="small"
                                />
                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={metric.description}
                                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                    error={!!errors[`description_${index}`]}
                                    helperText={errors[`description_${index}`]}
                                    required
                                    multiline
                                    rows={2}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            fontFamily: 'Poppins',
                                        }
                                    }}
                                    size="small"
                                />
                            </Stack>
                        ))}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
                    <Button
                        onClick={() => setOpenMetricForm(false)}
                        sx={{
                            textTransform: 'none',
                            fontFamily: 'Poppins',
                            color: '#666'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveMetric}
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            fontFamily: 'Poppins',
                            background: "linear-gradient(45deg, #0a0068 30%, #cc4049 90%)",
                            "&:hover": {
                                background: "linear-gradient(45deg, #1565c0 30%, #0a0068 90%)",
                            },
                        }}
                    >
                        {editingMetric ? 'Update All Metrics' : 'Add All Metrics'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default RatingMetricsDialog;
